import React, { useEffect, useState } from "react";
import { useAuth, API } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Plus, CheckCircle, Clock, DollarSign, Star } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AvailabilityEditor } from "@/components/AvailabilityEditor";
import { AddToCalendar } from "@/components/AddToCalendar";
import { ProfileTab } from "@/components/council/ProfileTab";
import { ServicesTab } from "@/components/council/ServicesTab";

export default function CouncilDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceForm, setServiceForm] = useState({ name: "", description: "", category: "", duration: 60, price: 0, currency: "INR" });
  const [nominateForm, setNominateForm] = useState({ nominee_name: "", nominee_email: "", nominee_linkedin: "", reason: "" });
  const [nominateOpen, setNominateOpen] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [pRes, sRes, bRes, eRes] = await Promise.all([
          fetch(`${API}/council/profile`, { credentials: "include" }),
          fetch(`${API}/council/services`, { credentials: "include" }),
          fetch(`${API}/council/bookings`, { credentials: "include" }),
          fetch(`${API}/council/earnings`, { credentials: "include" }),
        ]);
        if (pRes.ok) { const p = await pRes.json(); setProfile(p); setProfileForm(p); }
        if (sRes.ok) setServices(await sRes.json());
        if (bRes.ok) setBookings(await bRes.json());
        if (eRes.ok) setEarnings(await eRes.json());
      } finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/council/profile`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileForm),
      });
      if (!res.ok) throw new Error("Failed to save");
      const updated = await res.json();
      setProfile(updated);
      setEditingProfile(false);
      toast.success("Profile updated");
    } catch { toast.error("Failed to save profile"); }
    finally { setSaving(false); }
  };

  const publishProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/council/profile/publish`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed to publish");
      setProfile(p => ({ ...p, status: "published" }));
      toast.success("Profile is now live!");
    } catch { toast.error("Failed to publish profile"); }
    finally { setSaving(false); }
  };

  const addService = async () => {
    try {
      const res = await fetch(`${API}/council/services`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(serviceForm),
      });
      if (!res.ok) throw new Error("Failed");
      const svc = await res.json();
      setServices(s => [...s, svc]);
      setServiceForm({ name: "", description: "", category: "", duration: 60, price: 0, currency: "INR" });
      setShowServiceForm(false);
      toast.success("Service added");
    } catch { toast.error("Failed to add service"); }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm("Remove this service?")) return;
    try {
      await fetch(`${API}/council/services/${serviceId}`, { method: "DELETE", credentials: "include" });
      setServices(s => s.filter(svc => svc.service_id !== serviceId));
      toast.success("Service removed");
    } catch { toast.error("Failed to remove service"); }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const res = await fetch(`${API}/council/bookings/${bookingId}/status`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setBookings(b => b.map(bk => bk.booking_id === bookingId ? { ...bk, status, google_meet_link: data.meet_link || bk.google_meet_link } : bk));
      toast.success(`Booking ${status}${data.meet_link ? " — Meet link generated" : ""}`);
    } catch { toast.error("Failed to update booking"); }
  };

  const submitNomination = async () => {
    try {
      const res = await fetch(`${API}/council/nominate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nominateForm),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Nomination submitted");
      setNominateOpen(false);
      setNominateForm({ nominee_name: "", nominee_email: "", nominee_linkedin: "", reason: "" });
    } catch { toast.error("Failed to submit nomination"); }
  };

  const STATUS_COLOR = {
    confirmed: "bg-[#064E3B]/10 text-[#064E3B]",
    pending_payment: "bg-amber-50 text-amber-700",
    completed: "bg-[#F4F4F1] text-[#52525B]",
    cancelled: "bg-red-50 text-red-600",
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF8]"><Navbar />
      <div className="pt-24 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
            <div>
              <p className="section-label mb-1">Council Member Dashboard</p>
              <h1 className="text-3xl font-normal text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Welcome, {user?.name?.split(" ")[0]}
              </h1>
            </div>
            <div className="flex gap-3">
              {profile?.status !== "published" && (
                <button onClick={publishProfile} className="btn-primary text-sm flex items-center gap-2" data-testid="publish-profile-btn">
                  <CheckCircle size={14} /> Go Live
                </button>
              )}
              <Dialog open={nominateOpen} onOpenChange={setNominateOpen}>
                <DialogTrigger asChild>
                  <button className="btn-secondary text-sm flex items-center gap-2" data-testid="nominate-btn">
                    <Plus size={14} /> Nominate Expert
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-[#E4E4E1] rounded-sm">
                  <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Nominate a Professional</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    {["nominee_name", "nominee_email", "nominee_linkedin"].map((field) => (
                      <div key={field}>
                        <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">
                          {field.replace("nominee_", "").replace("_", " ")}
                        </label>
                        <input type="text" value={nominateForm[field]} onChange={e => setNominateForm(f => ({ ...f, [field]: e.target.value }))}
                          className="w-full border border-[#E4E4E1] text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#0F172A]" />
                      </div>
                    ))}
                    <div>
                      <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">Why do you recommend them?</label>
                      <textarea rows={3} value={nominateForm.reason} onChange={e => setNominateForm(f => ({ ...f, reason: e.target.value }))}
                        className="w-full border border-[#E4E4E1] text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#0F172A] resize-none" />
                    </div>
                    <button onClick={submitNomination} className="w-full btn-primary text-sm py-2.5" data-testid="submit-nomination-btn">Submit Nomination</button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Profile status banner */}
          {profile?.status !== "published" && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6 flex items-center gap-3">
              <Clock size={16} className="text-amber-600" />
              <p className="text-sm text-amber-700">Your profile is a <strong>draft</strong>. Complete it and click &ldquo;Go Live&rdquo; to become discoverable.</p>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Sessions", value: bookings.filter(b => b.status === "completed").length, icon: CheckCircle },
              { label: "Pending Requests", value: bookings.filter(b => b.status === "pending_payment").length, icon: Clock },
              { label: "Net Earnings", value: `₹${(earnings?.net_earnings || 0).toLocaleString("en-IN")}`, icon: DollarSign },
              { label: "Avg Rating", value: profile?.rating > 0 ? `${profile.rating}★` : "—", icon: Star },
            ].map((s) => (
              <div key={s.label} className="card-flat">
                <s.icon size={16} className="text-[#A1A1AA] mb-2" />
                <p className="text-2xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                <p className="text-xs text-[#52525B]">{s.label}</p>
              </div>
            ))}
          </div>

          <Tabs defaultValue="profile">
            <TabsList className="bg-[#F4F4F1] border border-[#E4E4E1] mb-5">
              <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
              <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
              <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
              <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
              <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <ProfileTab
                profile={profile}
                profileForm={profileForm}
                setProfileForm={setProfileForm}
                editing={editingProfile}
                setEditing={setEditingProfile}
                onSave={saveProfile}
                saving={saving}
              />
            </TabsContent>

            <TabsContent value="services">
              <ServicesTab
                services={services}
                showForm={showServiceForm}
                setShowForm={setShowServiceForm}
                form={serviceForm}
                setForm={setServiceForm}
                onAdd={addService}
                onDelete={deleteService}
              />
            </TabsContent>

            <TabsContent value="availability">
              <AvailabilityEditor />
            </TabsContent>

            <TabsContent value="bookings">
              <div className="space-y-3">
                {bookings.length === 0 ? (
                  <div className="card-flat text-center py-8 text-[#52525B] text-sm">No booking requests yet.</div>
                ) : bookings.map((b) => (
                  <div key={b.booking_id} className="card-flat" data-testid={`council-booking-${b.booking_id}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-semibold text-sm text-[#0F172A]">{b.service_name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${STATUS_COLOR[b.status] || "bg-[#F4F4F1] text-[#52525B]"}`}>{b.status.replace("_", " ")}</span>
                        </div>
                        <p className="text-xs text-[#52525B] mb-2">from {b.explorer_info?.name || b.explorer_name}</p>
                        <p className="text-xs text-[#A1A1AA]">₹{b.amount?.toLocaleString("en-IN")} · {b.duration} mins</p>
                        {b.notes && <p className="text-xs text-[#52525B] mt-1 italic">&ldquo;{b.notes}&rdquo;</p>}
                        {b.google_meet_link && (
                          <a href={b.google_meet_link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#064E3B] font-semibold hover:underline mt-1 block">
                            Join Meeting →
                          </a>
                        )}
                        {b.status === "confirmed" && (
                          <AddToCalendar booking={b} withName={b.explorer_info?.name || b.explorer_name} className="mt-2" />
                        )}
                      </div>
                      {b.status === "confirmed" && (
                        <button onClick={() => updateBookingStatus(b.booking_id, "completed")} className="btn-secondary text-xs py-1.5 px-3 shrink-0" data-testid={`complete-booking-${b.booking_id}`}>
                          Mark Complete
                        </button>
                      )}
                      {b.status === "pending_payment" && (
                        <button onClick={() => updateBookingStatus(b.booking_id, "confirmed")} className="btn-primary text-xs py-1.5 px-3 shrink-0" data-testid={`confirm-booking-${b.booking_id}`}>
                          Confirm
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="earnings">
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {[
                  { label: "Gross Revenue", value: `₹${(earnings?.total_revenue || 0).toLocaleString("en-IN")}` },
                  { label: "Platform Fee (15%)", value: `₹${(earnings?.platform_fee || 0).toLocaleString("en-IN")}` },
                  { label: "Net Earnings", value: `₹${(earnings?.net_earnings || 0).toLocaleString("en-IN")}` },
                ].map(s => (
                  <div key={s.label} className="card-flat text-center">
                    <p className="text-2xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                    <p className="text-xs text-[#52525B]">{s.label}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
